import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@material-ui/core";

/**
 * Professional replacement for window.confirm.
 *
 * Usage:
 *   <ConfirmDialog
 *     open={state.open}
 *     title="Delete location?"
 *     message={'"Queen Bee" will be removed. This cannot be undone.'}
 *     confirmLabel="Delete"
 *     variant="danger"
 *     busy={saving}
 *     onClose={() => setOpen(false)}
 *     onConfirm={() => doDelete()}
 *   />
 *
 * `onCancel` is for when the left button is itself a decision (e.g. "Discard
 * changes") rather than a dismissal. Without it the button just calls onClose;
 * tapping outside the dialog or pressing Esc always calls onClose.
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default", // 'default' | 'danger'
  busy = false,
  onClose,
  onCancel,
  onConfirm,
}) {
  const isDanger = variant === "danger";
  const confirmStyle = isDanger
    ? {
        background: "#dc2626",
        color: "#fff",
        boxShadow: "0 2px 8px rgba(220, 38, 38, 0.25)",
      }
    : {
        background: "#2d5a3d",
        color: "#fff",
        boxShadow: "0 2px 8px rgba(45, 90, 61, 0.25)",
      };

  const handleConfirm = () => {
    if (busy) return;
    if (onConfirm) onConfirm();
  };

  return (
    <Dialog
      open={!!open}
      onClose={busy ? undefined : onClose}
      aria-labelledby="pt-confirm-title"
      aria-describedby="pt-confirm-desc"
      PaperProps={{
        style: {
          borderRadius: 14,
          fontFamily: "'DM Sans', system-ui, sans-serif",
          minWidth: 320,
          maxWidth: 440,
          padding: "4px 0",
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.25)",
        },
      }}
    >
      {title && (
        <DialogTitle
          id="pt-confirm-title"
          disableTypography
          style={{
            padding: "20px 24px 6px",
            fontSize: 18,
            fontWeight: 700,
            color: "#1e293b",
            fontFamily: "inherit",
          }}
        >
          {title}
        </DialogTitle>
      )}
      <DialogContent style={{ padding: "6px 24px 10px" }}>
        <DialogContentText
          id="pt-confirm-desc"
          style={{
            color: "#475569",
            fontSize: 14,
            lineHeight: 1.5,
            margin: 0,
            fontFamily: "inherit",
          }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions style={{ padding: "10px 20px 16px", gap: 8 }}>
        <Button
          onClick={onCancel || onClose}
          disabled={busy}
          disableElevation
          style={{
            background: "#ffffff",
            color: "#334155",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: "8px 18px",
            fontWeight: 600,
            fontSize: 13,
            fontFamily: "inherit",
            textTransform: "none",
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={busy}
          disableElevation
          style={{
            ...confirmStyle,
            border: "none",
            borderRadius: 10,
            padding: "8px 18px",
            fontWeight: 600,
            fontSize: 13,
            fontFamily: "inherit",
            textTransform: "none",
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? "Working…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
